import qs from 'qs'
import { merge } from 'lodash'

import { FilterLogic } from '@SonarCX/pages/Customers/FieldFilters/FieldPills'

import { DEFAULT_EMBEDS } from '@SonarCX/providers/TaskAPIProvider'
import { TASK_FIELD_LIMIT } from '@SonarCX/providers/TaskPageStateProvider'
import { isUUID } from '@SonarCX/utilities'

const hasValue = val => val !== 'null'

const parseIDValue = (val, type) => {
  // No value, exclude param
  if (!hasValue(val)) {
    return {}
  }

  const id = `${type}_ids`
  return { [id]: [val] }
}

const parseDateValue = (val, map, id) => {
  // Ignore empty filter
  if (!hasValue(val)) {
    return {}
  }

  return { [id]: map[val] }
}

export const parseDatetimeValue = (val, id) => {
  // Ignore empty filter
  if (!hasValue(val)) {
    return {}
  }

  let value = val
  if (typeof val === 'string') {
    try {
      value = new Date(value).toISOString()
    } catch (error) {
      console.error('Invalid date value!', value)
      return {}
    }
  }

  if (typeof val === 'object') {
    try {
      value = val.toDate().toISOString()
    } catch (error) {
      console.error('Invalid date value!', value)
      return {}
    }
  }

  return {
    [id]: value
  }
}

export type CustomFilter = {
  field_id: string
  value: string
  logic: FilterLogic
}

const parseSearch = ({ searchFilter, fieldValues }) => {
  // api accepts an array of search terms but each has to be between 3 and 20 characters
  const trimmed = searchFilter.trim().slice(0, 20)
  const keywords = [trimmed]
  const allowedKeywords = keywords.filter(
    word => word.length >= 3 && word.length <= 20
  )

  const result = {
    filter_custom: fieldValues.slice(0, TASK_FIELD_LIMIT),
    keywords: allowedKeywords
  }

  if (fieldValues.length === 0) {
    delete result.filter_custom
  }

  if (allowedKeywords.length === 0) {
    delete result.keywords
  }

  return result
}

export const LEADS_FOR_ME = 'Tasks I Manage'
export const LEADS_FOR_USER = 'Tasks by Lead'
export const LEAD_OPTIONS = [LEADS_FOR_ME, LEADS_FOR_USER]

export const TASKS_I_OWN = 'Tasks I Own'
export const TASKS_OTHER_OWNER = 'Tasks by Owner'
export const OWNER_OPTIONS = [TASKS_I_OWN, TASKS_OTHER_OWNER]

const parseLeadValue = (val, user, leadID) => {
  // No value, filter is disabled
  if (!hasValue(val)) {
    return {}
  }

  if (val === LEADS_FOR_ME) {
    return { primary_assignee_id: user }
  }

  if (val === LEADS_FOR_USER) {
    if (leadID !== 'null') {
      return { primary_assignee_id: leadID }
    }
  }

  return {}
}

const parseOwnerValue = (val, user, onwerID) => {
  // No value, filter is disabled
  if (!hasValue(val)) {
    return {}
  }

  if (val === TASKS_I_OWN) {
    return { owner_id: user }
  }

  if (val === TASKS_OTHER_OWNER) {
    if (onwerID !== 'null') {
      return { owner_id: onwerID }
    }
  }

  return {}
}

export const MY_PRIVATE_TASKS = 'My private tasks'
export const TASK_ASSIGNED_TO_ME = 'Tasks assigned to me'
export const TASK_ASSIGNED_BY_ME = "Tasks I've assigned to others"
export const TASKS_FOR_USER = 'Tasks for a user'
export const TASK_OPTIONS = [
  MY_PRIVATE_TASKS,
  TASK_ASSIGNED_TO_ME,
  TASK_ASSIGNED_BY_ME,
  TASKS_FOR_USER
]

const parseTaskValue = (val, user, taskID) => {
  // No value, filter is disabled
  if (!hasValue(val)) {
    return {}
  }

  // Looking at someone else's
  if (val === TASKS_FOR_USER) {
    if (taskID !== 'null') {
      return { assigned_to: [taskID] }
    }
    return {}
  }

  // Your own tasks
  if (val === MY_PRIVATE_TASKS) {
    return {
      owner_id: user,
      assigned_to_only: user
    }
  }

  // Assigned to me
  if (val === TASK_ASSIGNED_TO_ME) {
    return { assigned_to: [user] }
  }

  // Assigned to others by me
  return { owner_id: user, assigned_to_other: user }
}

export const MY_WATCHED_TASKS = 'My watched tasks'
export const TASKS_FOR_WATCHER = 'Tasks for a watcher'
export const WATCHER_OPTIONS = [MY_WATCHED_TASKS, TASKS_FOR_WATCHER]

const parseWatcherValue = (val, user, watcherID) => {
  // No value, filter is disabled
  if (!hasValue(val)) {
    return {}
  }

  // Looking at someone else's
  if (val === TASKS_FOR_WATCHER) {
    if (watcherID !== 'null') {
      return { for_user_ids: [watcherID] }
    }
    return {}
  }

  // My watched tasks
  return { for_user_ids: [user] }
}

export const ALL_CLIENTS = 'All Clients'
export const TASKS_FOR_A_CLIENT = 'Tasks for a client'
export const CLIENT_OPTIONS = [TASKS_FOR_A_CLIENT]

const parseClientValue = (val, id) => {
  // No value, exclude param
  if (!hasValue(id)) {
    return {}
  }

  if (val === ALL_CLIENTS) {
    return {}
  }

  return {
    concerning_user_id: id
  }
}

const DUE_TODAY = 'Due today'
const DUE_THIS_WEEK = 'Due this week'
const DUE_THIS_MONTH = 'Due this month'
const DUE_IN_PAST = 'Overdue'
export const DUE_RANGE = 'Date Range'
export const DUE_MAP = {
  [DUE_TODAY]: 1,
  [DUE_THIS_WEEK]: 7,
  [DUE_THIS_MONTH]: 30,
  [DUE_IN_PAST]: 0
}
export const DUE_OPTIONS = [
  DUE_TODAY,
  DUE_THIS_WEEK,
  DUE_THIS_MONTH,
  DUE_IN_PAST,
  DUE_RANGE
]

const CREATED_TODAY = 'Created today'
const CREATED_THIS_WEEK = 'Created this week'
const CREATED_THIS_MONTH = 'Created this month'
export const CREATED_MAP = {
  [CREATED_TODAY]: 0,
  [CREATED_THIS_WEEK]: 7,
  [CREATED_THIS_MONTH]: 30
}
export const CREATED_OPTIONS = [
  CREATED_TODAY,
  CREATED_THIS_WEEK,
  CREATED_THIS_MONTH
]

export const VIEW_OPEN = 'View open and completed'
export const VIEW_CLOSED = 'View only closed'
// const VIEW_DELETED = 'View only deleted'
export const COMPLETED_OPTIONS = [VIEW_OPEN, VIEW_CLOSED] //, VIEW_DELETED]
const parseCompleteValue = val => {
  // No complete value set
  if (!hasValue(val)) {
    return {}
  }

  // Default view
  if (val === VIEW_OPEN) {
    return {}
  }

  // Only show closed
  if (val === VIEW_CLOSED) {
    return { closed_only: true }
  }

  // Deleted, unimplemented in API I think
  return {}
}

export const parseInclude = val => {
  // No value, exclude param
  if (!val) {
    return { embed: DEFAULT_EMBEDS }
  }

  return { include_subtasks: true, embed: ['parent', ...DEFAULT_EMBEDS] }
}

export const getIDs = (list: any[]) => list.map(item => item.id as string)

export const getNames = list => id => list.find(item => item.id === id).name

export const getURL = ({
  leadValue,
  leadID,
  taskValue,
  taskID,
  watcherValue,
  watcherID,
  categoryValue,
  statusValue,
  typeValue,
  priorityValue,
  dueValue,
  createdValue,
  completeValue,
  searchFilter,
  includeSubtasks,
  userID,
  dueStartValue,
  dueEndValue,
  sort,
  customSort,
  ownerValue,
  ownerID,
  fieldValues = {},
  clientValue,
  clientID,
  teamValue
}) => {
  let params = {}

  // Check for subtasks
  params = merge(params, parseInclude(includeSubtasks))

  // Check lead filtering
  params = merge(params, parseLeadValue(leadValue, userID, leadID))

  // Check owner filtering
  params = merge(params, parseOwnerValue(ownerValue, userID, ownerID))

  // Check task filtering
  params = merge(params, parseTaskValue(taskValue, userID, taskID))

  // Check watcher filtering
  params = merge(params, parseWatcherValue(watcherValue, userID, watcherID))

  // Check concerning user
  params = merge(params, parseClientValue(clientValue, clientID))

  // Check filtering for id based values
  params = merge(params, parseIDValue(categoryValue, 'category'))
  params = merge(params, parseIDValue(statusValue, 'status'))
  params = merge(params, parseIDValue(typeValue, 'type'))
  params = merge(params, parseIDValue(priorityValue, 'priority'))
  params = merge(params, parseIDValue(teamValue, 'team'))

  // Check date filtering options
  params = merge(params, parseDateValue(dueValue, DUE_MAP, 'due_within'))
  params = merge(
    params,
    parseDateValue(createdValue, CREATED_MAP, 'created_within')
  )

  // Check complete filtering
  params = merge(params, parseCompleteValue(completeValue))

  // Check keyword filtering
  params = merge(params, parseSearch({ searchFilter, fieldValues }))

  // Check due dates filtering
  params = merge(params, parseDatetimeValue(dueStartValue, 'due_start'))
  params = merge(params, parseDatetimeValue(dueEndValue, 'due_end'))
  let result = qs.stringify(params, { arrayFormat: 'brackets' })

  // Sorts
  if (customSort.length > 0) {
    result += `&custom_sort=${encodeURIComponent(customSort)}`
  } else if (sort.length > 0) {
    result += `&sort=${encodeURIComponent(sort)}`
  }

  return result
}

type ParsedSortID =
  | {
      custom: string
      normal: null
    }
  | {
      custom: null
      normal: string
    }
  | {
      custom: null
      normal: null
    }

const getSortID = (id: string = ''): ParsedSortID => {
  if (isUUID(id)) {
    return {
      custom: id.slice(0, 8),
      normal: null
    }
  }

  // Maps table accessor to api sort id
  const idMap = {
    title: 'title',
    due_date: 'due',
    status: 'status',
    type: 'type',
    updated_at: 'updated',
    category_id: 'category',
    priority: 'priority',
    concerning_user_id: 'userconcerning',
    created_by: 'createdby',
    created_at: 'created'
  }

  if (!(id in idMap)) {
    return {
      custom: null,
      normal: null
    }
  }

  return {
    custom: null,
    normal: idMap[id]
  }
}

type ParsedSort = {
  isCustom: boolean
  sort: string
}

const parseSort = (item: { id: string; desc: boolean }): ParsedSort => {
  const { id, desc } = item

  const prefix = desc ? '-' : '+'
  const { custom, normal } = getSortID(id)

  if (custom) {
    return {
      isCustom: true,
      sort: `${prefix}${custom}`
    }
  }

  if (normal) {
    return {
      isCustom: false,
      sort: `${prefix}${normal}`
    }
  }

  return null
}

export type SortDef = {
  id: string
  desc: boolean
}

export const parseSorts = (sorts: SortDef[]) => {
  const parsed = sorts?.map(item => parseSort(item)).filter(Boolean) ?? []
  const normal: string[] = []
  const custom: string[] = []

  parsed.forEach(({ isCustom, sort }) => {
    if (isCustom) {
      custom.push(sort)
    } else {
      normal.push(sort)
    }
  })

  return {
    normal,
    custom
  }
}
